import moment from "moment";
import { toSafeString } from "./strings";
import { courierList } from "@/maestros/courierValues";

export function escapeHtml(value, fallback = "") {
  return (toSafeString(value) || fallback)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMeasurement(value, unit, decimals = 2) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return `${numericValue.toFixed(decimals)} ${unit}`;
}

export function formatMinutes(value, fallback = "-") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return fallback;
  return `${Math.round(numericValue)} min`;
}

export function formatSecondsAsMinutes(value, fallback = "-") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return fallback;
  return formatMinutes(numericValue / 60, fallback);
}

export function compactClockLabel(
  value,
  { alwaysDropMinutes = false } = {}
) {
  const text = toSafeString(value);
  if (!text) return "";

  const match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return text;

  const [, hours, minutes] = match;
  if (alwaysDropMinutes) {
    return String(Number.parseInt(hours, 10));
  }

  return `${Number.parseInt(hours, 10)}:${minutes}`;
}

export function normalizeHexColor(
  value,
  fallback = "",
  { expandShort = true } = {}
) {
  const normalize = (candidate) => {
    const raw = toSafeString(candidate).toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(raw)) return raw;
    if (expandShort && /^#[0-9A-F]{3}$/.test(raw)) {
      const chars = raw.slice(1).split("");
      return `#${chars.map((char) => `${char}${char}`).join("")}`;
    }
    return "";
  };

  return normalize(value) || normalize(fallback);
}

export function toSoftColorFromHex(hexColor, alpha = 0.16) {
  const hex = normalizeHexColor(hexColor, "#5baee9").replace("#", "");
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function getAvatarText(value, abbreviation = "") {
  const shortCode = toSafeString(abbreviation).toUpperCase();
  if (shortCode) return shortCode.slice(0, 3);

  const label = toSafeString(value);
  if (!label) return "--";

  const words = label
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);

  if (!words.length) return "--";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
}

export function formatWeight(value) {
  return formatMeasurement(value, "kg");
}

export function formatVolume(value, decimals = 3) {
  return formatMeasurement(value, "m3", decimals);
}

export function normalizeAlerts(value) {
  if (typeof value === "string") {
    const text = toSafeString(value);
    return text ? [text] : [];
  }

  if (!Array.isArray(value)) return [];
  return value.map((item) => toSafeString(item)).filter(Boolean);
}

export function formatAlertsText(value, options = {}) {
  const { separator = " | " } = options;
  return normalizeAlerts(value).join(separator);
}

export function formatAlertsTooltipHtml(value) {
  const items = normalizeAlerts(value);
  if (items.length === 0) return "";
  if (items.length === 1) return escapeHtml(items[0]);

  const listItems = items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
  return `<div class="text-start"><ul class="mb-0 ps-3">${listItems}</ul></div>`;
}

function formatTooltipRows(rows) {
  const tooltipRows = rows
    .map(
      ([label, value]) =>
        `<div class="tooltip-grid-row"><strong class="tooltip-grid-label">${label}</strong><span class="tooltip-grid-value">${value}</span></div>`
    )
    .join("");

  return `<div class="tooltip-grid">${tooltipRows}</div>`;
}

export function hasDeliveryInfoSummary(item) {
  const status = item?.delivery_info?.status;
  return status && status !== "PENDIENTE";
}

function formatDeliveryInfoDate(value) {
  if (!value) return "-";
  return moment.utc(value).local().format("DD/MM/YYYY HH:mm");
}

export function formatDeliveryInfoSummary(item) {
  if (!hasDeliveryInfoSummary(item)) return "";

  const deliveryInfo = item?.delivery_info || {};
  const rows = [
    ["Fecha", formatDeliveryInfoDate(deliveryInfo.date)],
    ["Intentos reparto", deliveryInfo.attempts || "-"],
    ["Id. fiscal", deliveryInfo.identity_document || "-"],
    ["Observaciones", deliveryInfo.observations || "-"],
    ["Usuario", deliveryInfo.user || "-"],
  ];

  return formatTooltipRows(rows);
}

export function hasTaskDeliveryInfoSummary(item) {
  const status = item?.delivery_info?.status;
  return status && status !== "PENDIENTE";
}

function formatTaskDeliveryInfoDate(value) {
  const date = value?.$date || value;
  if (!date) return "-";
  return moment.utc(date).local().format("DD/MM/YYYY HH:mm");
}

export function formatTaskDeliveryInfoSummary(item) {
  if (!hasTaskDeliveryInfoSummary(item)) return "";

  const deliveryInfo = item?.delivery_info || {};
  const rows = [
    ["Fecha", formatTaskDeliveryInfoDate(deliveryInfo.date)],
  ];

  return formatTooltipRows(rows);
}

export const PLAN_TYPE_OPTIONS = [
  { value: "RECOGIDA", label: "Recogida" },
  { value: "ENTREGA", label: "Entrega" },
  { value: "AMBOS", label: "Ambos" },
];

export const HANDLING_TYPE_LABELS = {
  ESTANDAR: "Estándar",
  PALET: "Palé",
  VOLUMINOSO: "Voluminoso",
  MEDIOS_ESPECIALES: "Medios especiales",
  MERCANCIA_PELIGROSA: "Mercancía peligrosa",
  OTRO: "Otro",
};

export function formatHandlingType(value) {
  const key = toSafeString(value).trim().toUpperCase();
  return HANDLING_TYPE_LABELS[key] || "-";
}

export function formatCourier(courier) {
  const value = toSafeString(courier).trim();
  const known = courierList.find((item) => item.value === value.toUpperCase());
  if (known) return known.label;

  return value
    .toLocaleLowerCase("es-ES")
    .replace(/(?:^|\s)\S/g, (character) => character.toLocaleUpperCase("es-ES"));
}

export function hasTaskContentDescription(item) {
  return !!toSafeString(item?.content_description).trim();
}

export function formatTaskContentDescriptionTooltip(item) {
  if (!hasTaskContentDescription(item)) return "";

  const rows = [["Naturaleza", item.content_description]];
  return formatTooltipRows(rows);
}

export function formatStopTypeLabel(stopItem) {
  if (stopItem?.manual_type === "CARGA") return "Carga";
  if (stopItem?.manual_type === "DESCARGA") return "Descarga";

  const stopTypeValue = toSafeString(stopItem?.type || stopItem?.stop_type).toUpperCase();
  if (!stopTypeValue) return "";
  return `${stopTypeValue.slice(0, 1)}${stopTypeValue.slice(1).toLowerCase()}`;
}

export function formatAddressLabel(value, fallback = "") {
  if (!value || typeof value !== "object") return toSafeString(value) || fallback;

  const address = toSafeString(value?.address);
  const label = toSafeString(value?.label);
  const combined = [address, label].filter(Boolean).join(", ");

  return combined || fallback;
}

export function formatTimeWindow(value, formatClock = null) {

  const applyClockFormatter = (clockValue) => {
    const normalizedValue = toSafeString(clockValue);
    if (!normalizedValue) return "";
    if (typeof formatClock !== "function") return normalizedValue;
    return toSafeString(formatClock(normalizedValue));
  };

  if (typeof value === "string") {
    const normalizedValue = toSafeString(value);
    if (!normalizedValue) return "";

    const formattedValue =
      typeof formatClock === "function"
        ? normalizedValue.replace(/(\d{1,2}:\d{2})/g, (match) =>
            applyClockFormatter(match)
          )
        : normalizedValue;

    return formattedValue.replace(/\s*-\s*/g, "-");
  }

  if (!value || typeof value !== "object") return "";

  const start = applyClockFormatter(value?.start_time || value?.start);
  const end = applyClockFormatter(value?.end_time || value?.end);
  if (start && end) return `${start} - ${end}`;
  return start || end;
}
